import type { BboxTuple, LatLngLiteral } from "./types";

/** Copenhagen — shared camera fallback when no selection geometry exists. */
export const MAP_FALLBACK_CENTER: LatLngLiteral = { lat: 55.6761, lng: 12.5683 };

/**
 * Bounding box framing all of Denmark `[west, south, east, north]`. Used to open
 * a nationwide overview (e.g. company-search clusters) so the first viewport
 * fetch covers the whole country rather than the Copenhagen fallback.
 */
export const DENMARK_BOUNDS: BboxTuple = [7.8, 54.5, 15.3, 57.85];

/** Geographic centre of {@link DENMARK_BOUNDS} — nationwide camera without `fitBounds`. */
export const DENMARK_CENTER: LatLngLiteral = {
    lng: (DENMARK_BOUNDS[0] + DENMARK_BOUNDS[2]) / 2,
    lat: (DENMARK_BOUNDS[1] + DENMARK_BOUNDS[3]) / 2,
};

/** Navy selected-ejendom styling, used identically on both map panels. */
export const SELECTED_EJENDOM_COLOR = "#164573";

/**
 * Kort cadastral hit-fill. Mapbox does not fire layer `click` when fill-opacity
 * is 0, so idle must stay a hair above zero or taps on touch devices do nothing.
 */
export const PLOT_FILL_HOVER_OPACITY = 0.08;
export const PLOT_FILL_IDLE_OPACITY = 0.01;
export const PLOT_FILL_OPACITY = ["case", ["boolean", ["feature-state", "hover"], false], PLOT_FILL_HOVER_OPACITY, PLOT_FILL_IDLE_OPACITY] as const;

/**
 * The cadastral a user clicked, highlighted on both panels. Deliberately not
 * the navy above: a clicked cadastral is a different thing from the ejendom the
 * page is about, and the two are routinely on screen together. Kept separate
 * from `RESULT_PIN_HIGHLIGHT_COLOR` (same interaction blue today) so the
 * polygon and the pins can be retuned independently.
 */
export const SELECTED_CADASTRAL_COLOR = "#155eef";

/** Search-result pins on the kort map; the highlighted one (list hover) uses the brand color. */
export const RESULT_PIN_COLOR = "#475467";
export const RESULT_PIN_HIGHLIGHT_COLOR = "#155eef";

/** fitBounds padding shared by both panels so they frame selections alike. */
export const MAP_FIT_PADDING_PX = 32;

/**
 * Closest automatic fit on both panels. The live Estaid scale badge reads
 * **10m** at zoom 18 and **50m** at ~16.4; zoom 17 is the 30m stop. Caps
 * initial/fit cameras only — the maps themselves still allow zooming in.
 */
export const MAP_FIT_MAX_ZOOM = 17;

/** Extra inset for portfolio pins in a split rail — clears the mode bar and bottom-left stack. */
export const MAP_FIT_PADDING_PORTFOLIO_SPLIT = { top: 72, bottom: 96, left: 56, right: 40 } as const;

export type MapFitBoundsPadding = number | { top?: number; bottom?: number; left?: number; right?: number };

export interface MapFitBoundsOptions {
    padding: MapFitBoundsPadding;
    maxZoom: number;
    duration?: number;
    animate?: boolean;
}

/** Shared `fitBounds` options so both panels open at the 30m scale, not closer. */
export function mapFitBoundsOptions(overrides: Partial<Omit<MapFitBoundsOptions, "maxZoom">> = {}): MapFitBoundsOptions {
    return {
        padding: overrides.padding ?? MAP_FIT_PADDING_PX,
        maxZoom: MAP_FIT_MAX_ZOOM,
        ...(overrides.duration !== undefined ? { duration: overrides.duration } : {}),
        ...(overrides.animate !== undefined ? { animate: overrides.animate } : {}),
    };
}
